import type { FC, MouseEvent } from 'react';
import classNames from 'classnames';
import { Tooltip, type TooltipProps } from '@/shared/components/molecules/Tooltip';

export const colorStyles = {
  green: 'border-cf-green-1/20 bg-cf-green-4 text-cf-green-1',
  blue: 'border-[#1C3E6F] bg-[#1C2F4A] text-cf-blue-2',
  yellow: 'border-[#684C17] bg-[#463719] text-cf-orange-1',
  orange: 'border-[#DA6D46]/20 bg-[#2C1B0E] text-[#DA6D46]',
  purple: 'border-cf-blue-4/20 bg-[#322C3D] text-cf-blue-4',
  pink: 'border-cf-pink-1/20 bg-[#491A35] text-cf-pink-1',
  red: 'border-cf-red-1/20 bg-cf-red-4 text-cf-red-1',
  neutral: 'border-cf-gray-5 bg-cf-gray-4 text-cf-light-3',
  light: 'border-cf-light-1 bg-cf-gray-5 text-cf-light-3',
  white: 'border-cf-light-1 bg-cf-gray-5 text-cf-white',
  transparent: 'text-cf-white border-transparent',
  'neon-yellow': 'border-[#335818] bg-[#293A17] text-[#B6EB67]',
} as const;

const hoverColorStyles = {
  green: 'bg-[#15412B]',
  blue: 'bg-[#233A5C]',
  yellow: 'bg-[#52411E]',
  orange: 'bg-[#61391A]',
  purple: 'bg-[#433B51]',
  pink: 'bg-[#602145]',
  red: 'bg-[#441414]',
  neutral: 'bg-cf-gray-5',
  light: 'bg-cf-gray-5',
  white: 'bg-cf-gray-5',
  transparent: 'border-transparent text-cf-white',
  'neon-yellow': 'bg-[#32451F]',
};

type Color = keyof typeof colorStyles;

export { type Color as PillColor };

export type PillProps = {
  text?: string;
  Icon?: FC;
  color: Color;
  iconColor?: `text-${string}`;
  tooltipContent?: TooltipProps['content'];
  tooltipLink?: TooltipProps['link'];
  opacity?: `opacity-${string}`;
  maxWidth?: boolean;
  onClick?: (e: MouseEvent) => void;
};

export default function Pill({
  text,
  Icon,
  color,
  iconColor,
  tooltipContent,
  tooltipLink,
  opacity,
  maxWidth = true,
  onClick,
}: PillProps) {
  const pill = (
    <div
      role={onClick && 'button'}
      className={classNames(maxWidth && 'max-w-[125px]')}
      onClick={onClick}
    >
      <div
        className={classNames(
          'flex items-center rounded-full border',
          'h-[26px] max-w-fit px-1.5 font-aeonikMedium text-12 leading-none',
          colorStyles[color],
          opacity,
          onClick && `cursor-pointer duration-100 ease-out hover:${hoverColorStyles[color]}`,
        )}
      >
        {Icon && (
          <div
            className={
              ['neutral', 'light', 'white', 'transparent'].includes(color) ? iconColor : undefined
            }
          >
            <Icon />
          </div>
        )}
        {text && <div className="truncate px-0.5">{text}</div>}
      </div>
    </div>
  );

  if (tooltipContent)
    return (
      <Tooltip content={tooltipContent} link={tooltipLink}>
        {pill}
      </Tooltip>
    );

  return pill;
}
